import { Component, Input } from '@angular/core';
import { NzMessageService } from 'ng-zorro-antd/message';
import { DialogConfig } from 'src/app/modules/shared/components/alert-dialog/dialog-config';
import { WorkFlowAddCcRequest } from 'src/app/modules/shared/models/form/work-flow-add-cc-request.model';
import {
  OrganizationType,
  OrganizationTypeShortMap,
} from 'src/app/modules/shared/models/staff/organization-type.enum';
import { NzTreeNode } from 'src/app/modules/shared/models/tree-node.model';
import { WorkFlowUserService } from 'src/app/modules/shared/services/work-flow-user.service';

interface DialogPrCcFinishInput {
  postTreeNodeFull: NzTreeNode[];
  workFlowId: string;
  sectionLabelMap?: { [key: string]: string };
}

export interface DialogPrCcFinishOutput {
  cascaderValue: string[];
}

@Component({
  selector: 'app-dialog-pr-cc-finish',
  templateUrl: './dialog-pr-cc-finish.component.html',
  styleUrls: ['./dialog-pr-cc-finish.component.scss'],
})
export class DialogPrCcFinishComponent {
  @Input() data!: DialogConfig<DialogPrCcFinishInput>;

  cascaderOptions: NzTreeNode[] = [];

  cascaderValue: string[] = [];

  constructor(
    private workFlowUserService: WorkFlowUserService,
    private message: NzMessageService,
  ) {}

  ngOnInit() {
    const input = this.data?.data;
    this.buildCascaderOptions(
      input?.postTreeNodeFull ?? [],
      input?.sectionLabelMap,
    );
  }

  cancel() {
    this.data.onCancel();
  }

  confirm() {
    const req = this.buildAddCcRequest(this.cascaderValue);
    if (!req || !req.workFlowId) {
      this.message.error('抄送参数无效');
      return;
    }

    this.workFlowUserService.addCc(req).subscribe((ok) => {
      if (ok) {
        this.message.success('抄送成功');
        this.data.onConfirm?.();
      } else {
        this.message.error('抄送失败');
      }
    });
  }

  get canConfirm() {
    if (!this.cascaderValue || this.cascaderValue.length === 0) {
      return false;
    }
    return true;
  }

  private buildCascaderOptions(
    postTreeNodeFull: NzTreeNode[],
    sectionLabelMap?: { [key: string]: string },
  ) {
    this.cascaderOptions = [];

    // 使用深拷贝，确保不影响父组件传入的 postTreeNodeFull。
    const sourceNodes = JSON.parse(
      JSON.stringify(postTreeNodeFull ?? []),
    ) as NzTreeNode[];

    const typeNodeMap: { [key: string]: NzTreeNode } = {};
    for (const sectionNode of sourceNodes) {
      const filtedSectionNode = this.rebuildWithoutVirtualNodes(sectionNode);
      if (!filtedSectionNode) {
        continue;
      }

      const sectionValue = `${sectionNode.value ?? ''}`;
      const ids = sectionValue.split('#');
      if (ids.length < 2) {
        continue;
      }

      const sectionId = ids[0];
      const orgType = ids[1];
      if (!typeNodeMap[orgType]) {
        typeNodeMap[orgType] = {
          value: orgType,
          label:
            OrganizationTypeShortMap[orgType as OrganizationType] ?? orgType,
          children: [],
        };
        this.cascaderOptions.push(typeNodeMap[orgType]);
      }

      if (sectionLabelMap?.[sectionId]) {
        filtedSectionNode.label = sectionLabelMap[sectionId];
      }

      this.injectAllPeopleLeaves(filtedSectionNode);
      typeNodeMap[orgType].children!.push(filtedSectionNode);
    }
  }

  private rebuildWithoutVirtualNodes(sectionNode: NzTreeNode) {
    const resultSectionNode: NzTreeNode = {
      ...sectionNode,
      children: [],
    };

    for (const orgNode of sectionNode.children ?? []) {
      const resultOrgNode: NzTreeNode = {
        ...orgNode,
        children: [],
      };

      for (const deptNode of orgNode.children ?? []) {
        const deptId = this.getNodeIdByIndex(deptNode.value, 3);
        if (deptId !== undefined && deptId < 0) {
          continue;
        }

        const resultDeptNode: NzTreeNode = {
          ...deptNode,
          children: [],
        };

        for (const postNode of deptNode.children ?? []) {
          if (this.isLeafNode(postNode)) {
            continue;
          }

          const postId = this.getNodeIdByIndex(postNode.value, 4);
          if (postId !== undefined && postId < 0) {
            continue;
          }

          const resultPostNode: NzTreeNode = {
            ...postNode,
            children: [...(postNode.children ?? [])],
          };

          resultDeptNode.children!.push(resultPostNode);
        }

        if ((resultDeptNode.children ?? []).length > 0) {
          resultOrgNode.children!.push(resultDeptNode);
        }
      }

      if ((resultOrgNode.children ?? []).length > 0) {
        resultSectionNode.children!.push(resultOrgNode);
      }
    }

    if ((resultSectionNode.children ?? []).length === 0) {
      return undefined;
    }

    return resultSectionNode;
  }

  private getNodeIdByIndex(value: string | number | undefined, index: number) {
    const ids = `${value ?? ''}`.split('#');
    if (ids.length <= index) {
      return undefined;
    }
    const id = +ids[index];
    if (Number.isNaN(id)) {
      return undefined;
    }
    return id;
  }

  private injectAllPeopleLeaves(sectionNode: NzTreeNode) {
    for (const orgNode of sectionNode.children ?? []) {
      for (const deptNode of orgNode.children ?? []) {
        const deptUsers = this.collectUserLeafValuesDeep(deptNode);
        deptNode.label = this.withCount(deptNode.label, deptUsers.size);
        const deptAllValue = `${deptNode.value}#-1`;
        if (deptUsers.size > 0 && !this.hasLeaf(deptNode, deptAllValue)) {
          deptNode.children = deptNode.children ?? [];
          deptNode.children.unshift({
            value: deptAllValue,
            label: `全部人员(${deptUsers.size})`,
            isLeaf: true,
          });
        }

        for (const postNode of deptNode.children ?? []) {
          if (this.isLeafNode(postNode)) {
            continue;
          }
          const postUsers = this.collectUserLeafValues(postNode);
          postNode.label = this.withCount(postNode.label, postUsers.size);
          const postAllValue = `${postNode.value}#-1`;
          if (postUsers.size > 0 && !this.hasLeaf(postNode, postAllValue)) {
            postNode.children = postNode.children ?? [];
            postNode.children.unshift({
              value: postAllValue,
              label: `全部人员(${postUsers.size})`,
              isLeaf: true,
            });
          }
        }
      }
    }
  }

  private collectUserLeafValues(node: NzTreeNode) {
    const userLeafValues = new Set<string>();
    for (const child of node.children ?? []) {
      if (!this.isLeafNode(child)) {
        continue;
      }
      const value = `${child.value ?? ''}`;
      if (this.isUserLeafValue(value)) {
        userLeafValues.add(value);
      }
    }
    return userLeafValues;
  }

  private collectUserLeafValuesDeep(node: NzTreeNode) {
    const userLeafValues = new Set<string>();
    for (const child of node.children ?? []) {
      if (this.isLeafNode(child)) {
        const value = `${child.value ?? ''}`;
        if (this.isUserLeafValue(value)) {
          userLeafValues.add(value);
        }
        continue;
      }

      const subValues = this.collectUserLeafValuesDeep(child);
      for (const sub of subValues) {
        userLeafValues.add(sub);
      }
    }
    return userLeafValues;
  }

  private isUserLeafValue(value: string) {
    if (!value || value === '-') {
      return false;
    }
    const ids = value.split('#');
    return ids.length === 6 && ids[ids.length - 1] !== '-1';
  }

  private isLeafNode(node: NzTreeNode) {
    return node.isLeaf || !node.children || node.children.length === 0;
  }

  private hasLeaf(node: NzTreeNode, value: string) {
    for (const child of node.children ?? []) {
      if (`${child.value ?? ''}` === value) {
        return true;
      }
    }
    return false;
  }

  private withCount(label: string | number | undefined, count: number) {
    const text = `${label ?? ''}`.replace(/\(\d+\)$/g, '');
    return `${text}(${count})`;
  }

  private buildAddCcRequest(
    cascaderValue: string[],
  ): WorkFlowAddCcRequest | undefined {
    if (!cascaderValue || cascaderValue.length === 0) {
      return undefined;
    }

    const leafValue = cascaderValue[cascaderValue.length - 1];
    if (!leafValue || leafValue === '-') {
      return undefined;
    }

    const ids = leafValue.split('#');
    if (ids.length !== 5 && ids.length !== 6) {
      return undefined;
    }

    console.log('buildAddCcRequest', cascaderValue, ids);

    const req = new WorkFlowAddCcRequest();
    const workFlowId = +`${this.data?.data?.workFlowId ?? ''}`;
    if (!workFlowId) {
      return undefined;
    }
    req.workFlowId = workFlowId;

    const query = window.location.href.split('?')[1] ?? '';
    const params = new URLSearchParams(query);
    if (location.href.indexOf('#/open/') > -1) {
      req.token = params.get('token') ?? '';
    }

    const sectionId = +ids[0];
    const organizationId = +ids[2];
    const departmentId = +ids[3];
    // if (sectionId !== -1) {
    req.sectionId = sectionId;
    // }
    if (organizationId !== -1) {
      req.organizationId = organizationId;
    }
    if (departmentId !== -1) {
      req.departmentId = departmentId;
    }

    if (ids.length === 5) {
      // 部门全部，-1 不传
      return req;
    }

    const postId = +ids[4];
    const userId = +ids[5];
    if (postId !== -1) {
      req.postId = postId;
    }
    if (userId !== -1) {
      req.userId = userId;
    }
    return req;
  }
}
